import React from "react";
import { getPosFromKey } from "../chessUtils/util";
import styles from "./DrawLayer.module.css";
import pieceStyles from "./Piece.module.css";
import { COLOR_LETTERS, PIECE_LETTERS } from "../chessUtils/fen";

import PuzzleBadge from "./PuzzleBadge";
import MoveIcon from "@/components/common/move-icon";
import { ChessSets } from "@chesshotel/constants";
import { MoveType } from "@chesshotel/types";
import { Key, Pos } from "./types";
import { getPieceImageUrl } from "@/util/pieceAssetUrl";

// Calculate transform based on board position (0-7 coordinates from getPosFromKey)
// Uses pixels for precise alignment on high-DPI displays
function getSquareTransform(pos: Pos, squareSize: number): string {
  return `translate(${pos[0] * squareSize}px, ${pos[1] * squareSize}px)`;
}

function renderDot(pos: Pos, i: number) {
  const x1 = pos[0] * 12.5 + 12.5 / 2 + "%";
  const y1 = pos[1] * 12.5 + 12.5 / 2 + "%";

  return (
    <circle
      key={i}
      fill="#333333"
      opacity={0.15}
      cx={x1}
      cy={y1}
      r={"2%"}
    ></circle>
  );
}

function renderCircle(pos: Pos, i: number) {
  const x1 = pos[0] * 12.5 + 12.5 / 2 + "%";
  const y1 = pos[1] * 12.5 + 12.5 / 2 + "%";

  return (
    <circle
      key={i}
      stroke="#333333"
      fill="none"
      strokeWidth="1.1%"
      opacity={0.15}
      cx={x1}
      cy={y1}
      r={"5.2%"}
    ></circle>
  );
}

function renderMoveHelp(moveHelp, playingWhite: boolean, position) {
  const shapes = [];
  let lastKey = null;
  for (let i = 0; i < moveHelp.length; i++) {
    const key = moveHelp[i];
    if (key != lastKey) {
      lastKey = key;
      const pos = getPosFromKey(key, playingWhite);
      if (
        position[key] &&
        ((position[key].color == "white" && !playingWhite) ||
          (position[key].color == "black" && playingWhite))
      ) {
        shapes.push(renderCircle(pos, i));
      } else {
        shapes.push(renderDot(pos, i));
      }
    }
  }

  return (
    <div className="absolute z-10 w-full h-full">
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {shapes}
      </svg>
    </div>
  );
}

function renderLastMove(lastMove, lastMoveColor, chessSet, playingWhite: boolean, squareSize: number) {
  const fromPos = getPosFromKey(lastMove.from, playingWhite);
  const toPos = getPosFromKey(lastMove.to, playingWhite);

  let defaultColor = styles.lastMoveYellow;

  if (chessSet == ChessSets.STAUNTY) {
    defaultColor = styles.lastMoveForStaunty;
  } else if (chessSet == ChessSets.CHESSHOTEL_NEW) {
    defaultColor = styles.lastMoveForNew;
  }
  const colorClass = lastMoveColor ? lastMoveColor : defaultColor;

  return (
    <div className="flex overflow-hidden relative w-full h-full md:rounded">
      <div
        style={{ transform: getSquareTransform(fromPos, squareSize), width: squareSize, height: squareSize }}
        className={`${styles.lastMove} ${colorClass}`}
      />
      <div
        style={{ transform: getSquareTransform(toPos, squareSize), width: squareSize, height: squareSize }}
        className={`${styles.lastMove} ${colorClass}`}
      />
    </div>
  );
}

function renderHighlight(highLightKey: Key, playingWhite: boolean, squareSize: number) {
  const pos = getPosFromKey(highLightKey, playingWhite);
  return (
    <div
      style={{ transform: getSquareTransform(pos, squareSize), width: squareSize, height: squareSize }}
      className={styles.highlight}
    ></div>
  );
}

function renderMoveSquare(moveSquarePos: Pos, squareSize: number) {
  const squareOverlay = styles.squareOverlayYellow;
  return (
    <div
      style={{ transform: getSquareTransform(moveSquarePos, squareSize), width: squareSize, height: squareSize }}
      className={squareOverlay}
    ></div>
  );
}

export function renderCompletePuzzle(lastPuzzleMove, playingWhite: boolean, squareSize: number) {
  const pos = getPosFromKey(lastPuzzleMove.to, playingWhite);

  return (
    <div
      style={{ transform: getSquareTransform(pos, squareSize), width: squareSize, height: squareSize }}
      className="z-10 absolute"
    >
      <PuzzleBadge />
    </div>
  );
}

function renderGoast(goast, chessSet, squareSize: number) {
  const {
    goastPiece: { color, role },
    goastPos,
  } = goast;

  const pieceCode = COLOR_LETTERS[color] + PIECE_LETTERS[role];

  return (
    <div
      style={{
        backgroundImage: `url(${getPieceImageUrl(chessSet, pieceCode)})`,
        transform: getSquareTransform(goastPos, squareSize),
        width: squareSize,
        height: squareSize,
      }}
      className={`${pieceStyles.goast} ${pieceStyles.piece}`}
    ></div>
  );
}

function renderMoveIcon(moveType: MoveType, key: Key, playingWhite: boolean, squareSize: number) {
  if (!moveType) {
    return <></>;
  }
  const pos = getPosFromKey(key, playingWhite);

  return (
    <div
      style={{ transform: getSquareTransform(pos, squareSize), width: squareSize, height: squareSize }}
      className="z-[45] absolute"
    >
      <MoveIcon
        scaleWithSquare={true}
        addShadow={true}
        moveType={moveType}
        className="absolute top-[-12%] right-[-12%]"
      />
    </div>
  );
}

function renderHighlightSquares(highlightSquares, chessSet, playingWhite: boolean, squareSize: number) {
  return highlightSquares.map((square, i) => {
    const pos = getPosFromKey(square, playingWhite);

    let defaultColor = styles.lastMoveYellow;

    if (chessSet == ChessSets.STAUNTY) {
      defaultColor = styles.lastMoveForStaunty;
    }
    const colorClass = defaultColor;

    return (
      <div
        key={`highlight-${square}-${i}`}
        style={{ transform: getSquareTransform(pos, squareSize), width: squareSize, height: squareSize }}
        className={`${styles.lastMove} ${colorClass}`}
      ></div>
    );
  });
}

function renderSelectedSquare(key: Key, playingWhite: boolean, squareSize: number) {
  const pos = getPosFromKey(key, playingWhite);
  return (
    <div
      style={{ transform: getSquareTransform(pos, squareSize), width: squareSize, height: squareSize }}
      className={styles.selectedSquare}
    ></div>
  );
}

function DrawLayer({
  highLightKey,
  puzzleDone,
  playingWhite,
  goast,
  moveSquarePos,
  moveHelp,
  position,
  showLastMove,
  lastMove,
  lastMoveColor,
  settings,
  lastPuzzleMove,
  moveType,
  moveTypeKey,
  highlightSquares,
  squareSize,
  selectedKey,
}) {
  return (
    <>
      {moveType && moveTypeKey && renderMoveIcon(moveType, moveTypeKey, playingWhite, squareSize)}
      {lastMove &&
        (settings.lastMove || showLastMove) &&
        renderLastMove(lastMove, lastMoveColor, settings.chessSet, playingWhite, squareSize)}
      {moveHelp && renderMoveHelp(moveHelp, playingWhite, position)}
      {moveSquarePos && renderMoveSquare(moveSquarePos, squareSize)}
      {selectedKey && renderSelectedSquare(selectedKey, playingWhite, squareSize)}
      {highLightKey && renderHighlight(highLightKey, playingWhite, squareSize)}
      {goast && renderGoast(goast, settings.chessSet, squareSize)}
      {puzzleDone && renderCompletePuzzle(lastPuzzleMove, playingWhite, squareSize)}
      {highlightSquares &&
        renderHighlightSquares(highlightSquares, settings.chessSet, playingWhite, squareSize)}
    </>
  );
}

export default DrawLayer;
